import React from "react";
import {
  QuestionRadiogroupModel,
  QuestionFactory,
  Serializer,
  ItemValue,
} from "survey-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";

const QUESTION_TYPE = "genderRadioGroup";
let isRegistered = false;

/* =========================================================
   QUESTION MODEL
========================================================= */
class QuestionGenderRadioGroupModel extends QuestionRadiogroupModel {
  // constructor(name) {
  //   super(name);

  //   // Initialize choices properly
  //   this.createNewArray("choices");

  //   // Set default gender choices
  //   const defaultChoices = [
  //     new ItemValue("male", "Male"),
  //     new ItemValue("female", "Female"),
  //     new ItemValue("other", "Other"),
  //   ];
  //   this.setPropertyValue("choices", defaultChoices);

  //   // Set other defaults
  //   this.showOtherItem = false;
  //   this.showNoneItem = false;
  //   this.showSelectAllItem = false;
  //   this.hasOther = false;
  //   this.hasNone = false;
  //   this.hasSelectAll = false;

  //   this.colCount = 0;
  // }

  constructor(name) {
    super(name);

    if (!this.choices || this.choices.length === 0) {
      this.setPropertyValue("choices", [
        new ItemValue("male", "Male"),
        new ItemValue("female", "Female"),
        new ItemValue("other", "Other"),
      ]);
    }

    this.showOtherItem = false;
    this.showNoneItem = false;
    this.colCount = 0;
  }

  getType() {
    return QUESTION_TYPE;
  }
}

/* =========================================================
   REACT RENDERER - Simple Radio Group
========================================================= */
class SurveyQuestionGenderRadioGroup extends SurveyQuestionElementBase {
  get question() {
    return this.props.question;
  }

  handleChange = (value) => {
    this.question.value = value;
  };

  renderElement() {
    const q = this.question;
    const selectedValue = q.value;
    let choices = q.visibleChoices || q.choices || [];
    choices = choices.filter(
      (choice) =>
        choice.value !== "none" &&
        choice.value !== "newitem" &&
        choice.value !== "selectall",
    );

    return (
      <div style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {choices.map((choice, index) => {
            const isSelected = selectedValue === choice.value;
            const choiceId = `${q.id}_choice_${index}`;

            return (
              <div
                key={choice.value || index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 0",
                }}
              >
                {/* Radio button */}
                <div
                  style={{
                    position: "relative",
                    marginRight: "12px",
                    flexShrink: 0,
                  }}
                >
                  <input
                    type="radio"
                    id={choiceId}
                    checked={isSelected}
                    onChange={() => this.handleChange(choice.value)}
                    disabled={q.isReadOnly}
                    style={{
                      width: "18px",
                      height: "18px",
                      cursor: q.isReadOnly ? "not-allowed" : "pointer",
                      opacity: q.isReadOnly ? 0.6 : 1,
                      margin: 0,
                      appearance: "none",
                      border: "2px solid #adb5bd",
                      borderRadius: "50%",
                      outline: "none",
                      transition: "all 0.2s",
                      position: "relative",
                    }}
                  />

                  {/* Radio dot */}
                  {isSelected && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "8px",
                        height: "8px",
                        backgroundColor: "#007bff",
                        borderRadius: "50%",
                        pointerEvents: "none",
                        animation: "fadeIn 0.2s ease",
                      }}
                    />
                  )}
                </div>

                {/* Label */}
                <label
                  htmlFor={choiceId}
                  style={{
                    cursor: q.isReadOnly ? "not-allowed" : "pointer",
                    fontSize: "16px",
                    color: q.isReadOnly ? "#6c757d" : "#212529",
                    userSelect: "none",
                    flex: 1,
                    lineHeight: "24px",
                    display: "flex",
                    alignItems: "center",
                  }}
                  onClick={() =>
                    !q.isReadOnly && this.handleChange(choice.value)
                  }
                >
                  {choice.text}
                </label>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

/* =========================================================
   REGISTRATION - Working with SurveyJS 2.5.4
========================================================= */
export function registerGenderRadioGroupQuestion() {
  if (isRegistered) return;
  isRegistered = true;

  // Get properties from the built-in radiogroup by finding them individually
  const getRadioGroupProperty = (propName) => {
    // Try to find the property from radiogroup
    const prop = Serializer.findProperty("radiogroup", propName);
    return prop ? { ...prop } : null;
  };

  // Collect properties we want to inherit
  const inheritedProperties = [
    "name",
    "title",
    "description",
    "isRequired",
    "readOnly",
    "choices",
    "colCount",
    "showOtherItem",
    "showNoneItem",
    "otherText",
    "noneText",
    "visibleIf",
    "enableIf",
  ];

  const properties = [];

  // Add name property first with explicit text type
  properties.push({
    name: "name",
    type: "text",
    category: "general",
    visible: true,
    isRequired: true,
    isLocalizable: false,
    onSetValue: function (obj, value) {
      obj.setPropertyValue("name", value);
    },
  });

  // Add other properties
  properties.push({
    name: "title:text",
    category: "general",
    visible: true,
  });

  properties.push({
    name: "description:text",
    category: "general",
    visible: true,
  });

  properties.push({
    name: "isRequired:boolean",
    category: "general",
    visible: true,
  });

  properties.push({
    name: "readOnly:boolean",
    category: "general",
    visible: true,
  });

  // ✅ CRITICAL: Add choices property with proper configuration
  properties.push({
    name: "choices:itemvalues",
    category: "choices",
    displayName: "Gender Options",
    visible: true,
    isRequired: false,
    // default: [
    //   { value: "male", text: "Male" },
    //   { value: "female", text: "Female" },
    //   { value: "other", text: "Other" },
    // ],
    // onSetValue: function (obj, value) {
    //   if (Array.isArray(value)) {
    //     const choices = value.map((item) =>
    //       item instanceof ItemValue
    //         ? item
    //         : new ItemValue(item.value, item.text || item.value),
    //     );
    //     obj.setPropertyValue("choices", choices);
    //   }
    // },

    onSetValue: function (obj, value) {
      if (Array.isArray(value)) {
        obj.setPropertyValue(
          "choices",
          value.map((item) =>
            item instanceof ItemValue
              ? item
              : new ItemValue(item.value, item.text || item.value),
          ),
        );
      }
    },

    // onGetValue: function (obj) {
    //   return obj.choices;
    // },
  });

  // Add other radio group properties
  properties.push({
    name: "colCount:number",
    category: "layout",
    default: 0,
    visible: true,
    choices: [0, 1, 2, 3, 4],
  });

  properties.push({
    name: "showOtherItem:boolean",
    category: "choices",
    default: false,
    visible: true,
  });

  properties.push({
    name: "showNoneItem:boolean",
    category: "choices",
    default: false,
    visible: true,
  });

  properties.push({
    name: "otherText:text",
    category: "choices",
    visible: true,
    default: "Other",
  });

  properties.push({
    name: "noneText:text",
    category: "choices",
    visible: true,
    default: "None",
  });

  // Register the class
  Serializer.addClass(
    QUESTION_TYPE,
    properties,
    () => new QuestionGenderRadioGroupModel(""),
    "radiogroup",
  );

  // ✅ Ensure choices property is editable
  setTimeout(() => {
    // Fix name property
    const nameProp = Serializer.findProperty(QUESTION_TYPE, "name");
    if (nameProp) {
      nameProp.type = "text";
      nameProp.inputType = "text";
      delete nameProp.dateFormat;
    }

    // Ensure choices property is editable
    const choicesProp = Serializer.findProperty(QUESTION_TYPE, "choices");
    if (choicesProp) {
      choicesProp.readOnly = false;
      choicesProp.visible = true;
      choicesProp.isRequired = false;
    }
  }, 0);

  setTimeout(() => {
    const unwantedProperties = [
      "showOtherItem",
      "showNoneItem",
      "showSelectAllItem",
      "hasOther",
      "hasNone",
      "hasSelectAll",
      "otherText",
      "noneText",
      "selectAllText",
      "otherPlaceholder",
      "otherErrorText",
      "colCount",
      "separateSpecialChoices",
    ];

    unwantedProperties.forEach((propName) => {
      const prop = Serializer.findProperty(QUESTION_TYPE, propName);
      if (prop) {
        prop.visible = false;
        prop.readOnly = true;
      }
    });

    // Also hide from parent class inheritance
    const parentProps = Serializer.getProperties("radiogroup");
    if (parentProps) {
      unwantedProperties.forEach((propName) => {
        const parentProp = parentProps.find((p) => p.name === propName);
        if (parentProp) {
          parentProp.visible = false;
        }
      });
    }
  }, 0);

  // Register with QuestionFactory
  QuestionFactory.Instance.registerQuestion(
    QUESTION_TYPE,
    (name) => new QuestionGenderRadioGroupModel(name),
  );

  // Register React component
  ReactQuestionFactory.Instance.registerQuestion(QUESTION_TYPE, (props) =>
    React.createElement(SurveyQuestionGenderRadioGroup, props),
  );
}

registerGenderRadioGroupQuestion();

export default QuestionGenderRadioGroupModel;
